import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { DialogBody, DialogFooter } from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components/Select';
import type { Category, CreateCategoryInput, UpdateCategoryInput } from '../../../voice-agent/types/scenarios';
import { styleSheet } from './styles';

const ROLE_OPTIONS = [
	{ value: 'SDR', label: 'SDR' },
	{ value: 'AE', label: 'AE' },
];

interface IProps {
	category?: Category | null;
	onSubmit: (input: CreateCategoryInput | UpdateCategoryInput) => Promise<void>;
	onCancel: () => void;
	isSubmitting: boolean;
}

export const CategoryForm: React.FC<IProps> = ({ category, onSubmit, onCancel, isSubmitting }) => {
	const [name, setName] = React.useState(category?.name || '');
	const [description, setDescription] = React.useState(category?.description || '');
	const [role, setRole] = React.useState(category?.role || '');
	const [active, setActive] = React.useState(category?.active ?? true);
	const [errors, setErrors] = React.useState<{ name?: string; role?: string }>({});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		const newErrors: { name?: string; role?: string } = {};

		if (!name.trim()) {
			newErrors.name = 'Name is required';
		}
		if (!role) {
			newErrors.role = 'Role is required';
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setErrors({}); // Clear errors on valid submit

		const input: CreateCategoryInput | UpdateCategoryInput = {
			name: name.trim(),
			description: description.trim(),
			role,
			active,
		};

		await onSubmit(input);
	};

	return (
		<form onSubmit={handleSubmit}>
			<DialogBody>
				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='name' className={css(styleSheet.label)}>
						Name <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input
						type='text'
						id='name'
						value={name}
						onChange={e => {
							setName(e.target.value);
							if (errors.name) {
								setErrors(prev => ({ ...prev, name: undefined }));
							}
						}}
						placeholder='Enter category name'
						required
						disabled={isSubmitting}
					/>
					{errors.name && <span className={css(styleSheet.error)}>{errors.name}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='description' className={css(styleSheet.label)}>
						Description
					</label>
					<TextArea
						id='description'
						value={description}
						onChange={e => setDescription(e.target.value)}
						placeholder='Enter category description (optional)'
						rows={3}
						disabled={isSubmitting}
					/>
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='role' className={css(styleSheet.label)}>
						Role <span className={css(styleSheet.required)}>*</span>
					</label>
					<Select
						value={role}
						onValueChange={value => {
							setRole(value);
							if (errors.role) {
								setErrors(prev => ({ ...prev, role: undefined }));
							}
						}}
						disabled={isSubmitting}
					>
						<SelectTrigger>
							<SelectValue placeholder='Select a role' />
						</SelectTrigger>
						<SelectContent>
							{ROLE_OPTIONS.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{errors.role && <span className={css(styleSheet.error)}>{errors.role}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.checkboxLabel)}>
						<Checkbox
							checked={active}
							onCheckedChange={checked => setActive(checked === true)}
							disabled={isSubmitting}
						/>
						<span className={css(styleSheet.checkboxText)}>Active</span>
					</label>
				</div>
			</DialogBody>

			<DialogFooter>
				<Button type='button' variant='secondary' onClick={onCancel} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button type='submit' variant='primary' disabled={isSubmitting || !name.trim() || !role}>
					{isSubmitting ? 'Saving...' : category ? 'Update' : 'Create'}
				</Button>
			</DialogFooter>
		</form>
	);
};
